import { keyframes } from '@emotion/react'
import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { MultiStyleType } from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

export enum CallSoundIndicatorVariants {
  Active = 'active',
  Hold = 'hold',
  Mute = 'mute',
  Reconnecting = 'reconnecting',
}

const sizeMap = {
  [Size.sm]: {
    fullSize: '280px',
    coreSize: '200px',
  },
  [Size.md]: {
    fullSize: '360px',
    coreSize: '240px',
  },
  [Size.lg]: {
    fullSize: '440px',
    coreSize: '280px',
  },
}

// Use scale transforms instead of width/height animation to avoid subpixel layout jitter
const pulseSm = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(${parseInt(sizeMap[Size.sm].coreSize) / parseInt(sizeMap[Size.sm].fullSize)});
  }
`

const pulseMd = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(${parseInt(sizeMap[Size.md].coreSize) / parseInt(sizeMap[Size.md].fullSize)});
  }
`

const pulseLg = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(${parseInt(sizeMap[Size.lg].coreSize) / parseInt(sizeMap[Size.lg].fullSize)});
  }
`

const callSoundIndicatorParts = [
  'container',
  'pulse',
  'core',
  'icon',
  'text',
] as const

const helpers = createMultiStyleConfigHelpers(callSoundIndicatorParts)

export const CallSoundIndicatorStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      background: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
    },
    pulse: {
      borderRadius: 'full',
      alignItems: 'center',
      justifyContent: 'center',
      willChange: 'transform',
    },
    core: {
      borderRadius: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      boxSize: '90px',
    },
    text: {
      color: 'brand.contrast',
      fontSize: '16px',
      fontWeight: 'bold',
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        boxSize: sizeMap[Size.sm].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.sm].fullSize,
        animation: `${pulseSm} 1s ease-in-out infinite alternate`,
      },
      core: {
        boxSize: sizeMap[Size.sm].coreSize,
      },
      icon: {},
    },
    [Size.md]: {
      container: {
        boxSize: sizeMap[Size.md].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.md].fullSize,
        animation: `${pulseMd} 1s ease-in-out infinite alternate`,
      },
      core: {
        boxSize: sizeMap[Size.md].coreSize,
      },
      icon: {},
    },
    [Size.lg]: {
      container: {
        boxSize: sizeMap[Size.lg].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.lg].fullSize,
        animation: `${pulseLg} 1s ease-in-out infinite alternate`,
      },
      core: {
        boxSize: sizeMap[Size.lg].coreSize,
      },
      icon: {
        boxSize: '100px',
      },
    },
  },
  variants: {
    // Active shows the Mango logo mark — pinned to the non-themable blue
    // palette (identical to brand in the default theme) so partner themes
    // never recolor Mango's own branding.
    [CallSoundIndicatorVariants.Active]: {
      pulse: {
        background: 'blue.muted',
      },
      core: {
        background: 'gradients.mangoBrand.call_sound_indicator',
      },
      icon: {
        color: 'blue.solid',
      },
    },
    [CallSoundIndicatorVariants.Hold]: {
      pulse: {
        background: 'bg.emphasized',
      },
      core: {
        background: 'bg.muted',
      },
      icon: {
        color: 'brand.contrast',
      },
    },
    [CallSoundIndicatorVariants.Mute]: {
      pulse: {
        background: 'brand.subtle',
      },
      core: {
        background: 'brand.muted',
      },
      icon: {
        color: 'brand.contrast',
      },
    },
    [CallSoundIndicatorVariants.Reconnecting]: {
      pulse: {
        background: 'bg.emphasized',
      },
      core: {
        background: 'bg.muted',
      },
      icon: {
        color: 'brand.contrast',
        boxSize: '60px',
        margin: '10px',
      },
    },
  },
  defaultProps: {
    size: Size.sm,
    variant: CallSoundIndicatorVariants.Active,
  },
})

export const useCallSoundIndicatorStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(
    CallSoundIndicatorStyle,
    props,
  ) as MultiStyleType<typeof callSoundIndicatorParts>
}
